import {
  LiliNotification,
  LiliNotificationType,
  LiliToastDefaultConfig,
} from '@lili/lili-frontend-common';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { useEffect } from 'react';
import { useBoundStore } from '@Store/useBoundStore';
import { useCountdownTimer } from '@Hooks/useTimer';
import { RoutesMap } from '@Router/router.types';
import { OnboardingFormFields } from '@Types/index';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useFormHook, useSetError } from '@Hooks/index';
import { useSendMfaCode, useValidateAuthorizedUserMfa } from '../api/verifyPhone.api';

export const phoneFormScheme = z.object({
  [OnboardingFormFields.MFA_OTP]: z.string(),
});

type PhoneForm = z.infer<typeof phoneFormScheme>;

export const useVerifyPhoneLogic = () => {
  const { navigateToNextPage } = useNavigationManager();
  const { mutateAsync: validateMfa, isPending: isLoading } = useValidateAuthorizedUserMfa();
  const { mutateAsync: sendMfaCode, isPending: isSendingMfaCode } = useSendMfaCode();
  const { t } = useTranslation(['inviteTeamMember']);
  const setAccessToken = useBoundStore.getState().setAuthUserAccessToken;

  const { startTimer, timeLeft } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });

  useEffect(() => {
    startTimer();
  }, []);

  const handleSendMfaCode = async () => {
    await sendMfaCode();
  };

  const defaultValues: PhoneForm = {
    [OnboardingFormFields.MFA_OTP]: '',
  };

  const formMethods = useFormHook({
    formSchema: phoneFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'otp-phone-form',
    reValidateMode: 'onBlur',
  });

  const { isValid } = useFormState({ control: formMethods.control });

  const handleVerifyCode = async (code: string) => {
    const response = await validateMfa({
      answer: code,
    });
    setAccessToken(response.accessToken);
    navigateToNextPage(RoutesMap.VERIFY_PHONE_NUMBER);
  };

  const handleResendCode = async () => {
    try {
      await handleSendMfaCode();
      startTimer();
      toast(
        <LiliNotification
          type={LiliNotificationType.SUCCESS}
          text={t('verifyPhone.resendCodeSuccess')}
          id='resend-otp-success'
        />,
        { ...LiliToastDefaultConfig, closeOnClick: true }
      );
    } catch {
      toast(
        <LiliNotification
          type={LiliNotificationType.ERROR}
          text={t('verifyPhone.resendCodeError')}
          id='resend-otp-failure'
        />,
        { ...LiliToastDefaultConfig, closeOnClick: true }
      );
    }
  };

  useSetError({ formMethods });

  return {
    handleVerifyCode,
    handleResendCode,
    isLoading,
    isSendingMfaCode,
    isValid,
    formMethods,
    handleSendMfaCode,
    timeLeft,
  };
};
